import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import { useFilterContext } from '../contexts/FilterProvider';
import { NO_PRIMARY_FILTER_KEY } from '../engine/filterGroupConsistency';
import {
  FilterType,
  type PrimaryFilterId,
  type PrimarySelection,
  type TreeFilter,
} from '../types/filters';
import { isPrimaryFilterId } from '../utils/common';
import { useRecentEntities } from './useRecentEntities';

type PrimaryFilterStateResult = {
  primaryFilter: TreeFilter | null;
  activePrimaryFilterLength: number;
  removePrimaryFilter: (selectionId: string) => void;
  reorderPrimarySelections: (newPrimarySelections: PrimarySelection[]) => void;
};

const noop = () => undefined;

export const usePrimaryFilterState = (
  filterId?: PrimaryFilterId
): PrimaryFilterStateResult => {
  const { dispatch, filterState, activeFilterMap } = useFilterContext();
  const { primaryFilter: tabContentPrimaryFilter } = useTabContentConfig();
  const { addRecents } = useRecentEntities(filterId);

  if (!filterId || !tabContentPrimaryFilter) {
    return {
      primaryFilter: null,
      activePrimaryFilterLength: 0,
      removePrimaryFilter: noop,
      reorderPrimarySelections: noop,
    };
  }

  const activeKey =
    filterState.activeKey ??
    tabContentPrimaryFilter.id ??
    NO_PRIMARY_FILTER_KEY;
  const primaryFilter = (() => {
    if (!isPrimaryFilterId(activeKey)) {
      return null;
    }
    const filter = activeFilterMap.get(activeKey);
    if (filter?.type === FilterType.TREE && filter.isPrimary) {
      return filter;
    }
    return null;
  })();

  const primarySelections = primaryFilter?.primarySelections ?? [];

  const removePrimaryFilter = (selectionId: string) => {
    if (primarySelections.length <= 1) {
      return;
    }
    const removed = primarySelections.find(
      (selection) => selection.id === selectionId
    );
    if (removed) {
      addRecents([removed]);
    }
    dispatch({
      type: 'REMOVE_PRIMARY_SELECTION',
      payload: { filterId, selectionId },
    });
  };

  const reorderPrimarySelections = (
    newPrimarySelections: PrimarySelection[]
  ) => {
    dispatch({
      type: 'REORDER_PRIMARY_SELECTIONS',
      payload: { filterId, newPrimarySelections },
    });
  };

  return {
    primaryFilter,
    activePrimaryFilterLength: primarySelections.length,
    removePrimaryFilter,
    reorderPrimarySelections,
  };
};
